import { Radio } from 'ui/radio';
import { InputText } from 'ui/inputs';
import { useState, FunctionComponent, useEffect } from 'react';
import flowRight from 'lodash/flowRight';
import { RUMHelpers } from 'services/observability/datadog';
import { createSearchQueryWithSingleRequirement } from 'modules/segmentation/navigation/utils/createSearchQueryWithSingleRequirement';
import { ClientDataType, FieldType, NumericComparator } from 'modules/queryBuilder/types';
import { createActionName } from '../observabilityUtils';
import {
  CustomDash,
  DistributionGraphContainerTop,
  DistributionGraphContainerBottom,
  HistogramContainer,
  RadioGroupWrapper,
  RadioLabelWrapper,
  RadioCustomRangeText,
  RadioSelectionText,
  RadioSelectionTextSecondary,
  RadioSelectionTitle,
  SliderContainer,
  StyledRadioPercentSign,
  StyledSlider,
} from './charts/Styled';
import {
  BOTTOM_10_PERCENT,
  CUSTOM_RANGE,
  DIST_GRAPH_RADIO_OPTIONS,
  ITBLDS_PG_PREFIX,
  LEAST_LIKELY_TO_CONVERT,
  MOST_LIKELY_TO_CONVERT,
  PREDICTIVE_GOALS_KEY,
  PRG_PROBABILITY_SCORE_TYPE,
  RANGE_SLIDER_CONTACT_SELECTION,
  TEST_IDS,
  TOP_10_PERCENT,
} from '../constants';
import { DistributionGraph } from './charts/DistributionGraph';
import { DistributionSelectionData } from './charts/DistributionSelectionData';
import {
  DistributionChartWithRadioSelectorsProps,
  PredictiveGoalModelDetails,
  SegmentationParams,
} from '../types';
import { useGoalGraphContext } from './contexts/GoalGraphContext';
import {
  createXAxisData,
  createYAxisData,
  getBoundedValues,
  transformDataFromSlider,
  transformDataToSlider,
} from './charts/utils/dataHelpers';
import { isValidRange, RANGE_INPUTS } from '../chartUtils';

export const createSegmentationSearchQuery = (profileField: string, min: number, max: number) =>
  createSearchQueryWithSingleRequirement(ClientDataType.User, [
    {
      dataType: ClientDataType.User,
      field: `${ITBLDS_PG_PREFIX}${profileField}.${PRG_PROBABILITY_SCORE_TYPE}`,
      fieldType: FieldType.FLOAT,
      comparatorType: NumericComparator.GREATER_THAN_OR_EQUAL,
      value: String(min),
    },
    {
      dataType: ClientDataType.User,
      field: `${ITBLDS_PG_PREFIX}${profileField}.${PRG_PROBABILITY_SCORE_TYPE}`,
      fieldType: FieldType.FLOAT,
      comparatorType: NumericComparator.LESS_THAN_OR_EQUAL,
      value: String(max),
    },
  ]);

export const DistributionChartWithRadioSelectors: FunctionComponent<
  React.PropsWithChildren<DistributionChartWithRadioSelectorsProps>
> = ({ goal }) => {
  const {
    distributionData: { range },
    updateDistributionMin,
    updateDistributionMax,
  } = useGoalGraphContext();

  const { modelDetails } = goal;

  const [rangeMinInputVal, setRangeMinInputVal] = useState(range.minPoint);
  const [rangeMaxInputVal, setRangeMaxInputVal] = useState(range.maxPoint);

  const [isRangeMinInputValInvalid, setIsRangeMinInputValInvalid] = useState(false);
  const [isRangeMaxInputValInvalid, setIsRangeMaxInputValInvalid] = useState(false);

  const updateMin = flowRight(updateDistributionMin, transformDataFromSlider);
  const updateMax = flowRight(updateDistributionMax, transformDataFromSlider);

  const [selectionType, setSelectionType] = useState(DIST_GRAPH_RADIO_OPTIONS.MOST_LIKELY);
  const sliderMin = transformDataToSlider(range.minPoint);
  const sliderMax = transformDataToSlider(range.maxPoint);
  const { xMin, xMax, xPoints } = createXAxisData();
  const { yMin, yMax, yPoints } = createYAxisData(
    (modelDetails as Required<PredictiveGoalModelDetails>).prob_at_each_percentile,
  );

  useEffect(() => {
    const { errorType } = isValidRange({ min: rangeMinInputVal, max: rangeMaxInputVal });

    setIsRangeMinInputValInvalid(false);
    setIsRangeMaxInputValInvalid(false);

    if (errorType.includes(RANGE_INPUTS.MAX) && errorType.includes(RANGE_INPUTS.MIN)) {
      setIsRangeMinInputValInvalid(true);
      setIsRangeMaxInputValInvalid(true);
    } else if (errorType.includes(RANGE_INPUTS.MIN)) {
      setIsRangeMinInputValInvalid(true);
    } else if (errorType.includes(RANGE_INPUTS.MAX)) {
      setIsRangeMaxInputValInvalid(true);
    }
  }, [rangeMinInputVal, rangeMaxInputVal, range.maxPoint, range.minPoint]);

  const handleNavigateToSegmentation = () => {
    const [boundedMin, boundedMax] = getBoundedValues(
      (modelDetails as Required<PredictiveGoalModelDetails>).prob_at_each_percentile,
      sliderMin,
      sliderMax,
    );

    const goalSegmentationParams: SegmentationParams = {
      id: goal.id,
      name: goal.userProfileField,
      min: boundedMin,
      max: boundedMax,
      scoreType: PRG_PROBABILITY_SCORE_TYPE,
    };

    RUMHelpers.addRUMAction(
      createActionName('Redirect to segmentation page with segmentation parameters'),
      { goalSegmentationparams: goalSegmentationParams },
    );

    // Consumed by angular users/segmentList.controller.js
    localStorage.setItem(PREDICTIVE_GOALS_KEY, JSON.stringify(goalSegmentationParams));
    window.location.pathname = 'segmentation';
  };

  const [boundedMin, boundedMax] = getBoundedValues(
    (modelDetails as Required<PredictiveGoalModelDetails>).prob_at_each_percentile,
    sliderMin,
    sliderMax,
  );

  const searchQuery = createSegmentationSearchQuery(goal.userProfileField, boundedMin, boundedMax);

  const onMostLikelyCallback = () => {
    updateMin(0.9);
    updateMax(1);
    setRangeMinInputVal(90);
    setRangeMaxInputVal(100);
    setSelectionType(DIST_GRAPH_RADIO_OPTIONS.MOST_LIKELY);
  };

  const onLeastLikelyCallback = () => {
    updateMin(0);
    updateMax(0.1);
    setRangeMinInputVal(0);
    setRangeMaxInputVal(10);
    setSelectionType(DIST_GRAPH_RADIO_OPTIONS.LEAST_LIKELY);
  };

  const onCustomCallback = () => {
    setSelectionType(DIST_GRAPH_RADIO_OPTIONS.CUSTOM);
  };

  const onMinRangeInputChange = (value: string) => {
    const truncatedNumericVal = Number(value.substring(0, 3));
    setRangeMinInputVal(truncatedNumericVal);
    const { isValid } = isValidRange({ min: truncatedNumericVal, max: range.maxPoint });
    if (isValid) {
      updateMin(transformDataToSlider(truncatedNumericVal));
    }
  };

  const onMaxRangeInputChange = (value: string) => {
    const truncatedNumericVal = Number(value.substring(0, 3));
    setRangeMaxInputVal(truncatedNumericVal);
    const { isValid } = isValidRange({ min: range.minPoint, max: truncatedNumericVal });
    if (isValid) {
      updateMax(transformDataToSlider(truncatedNumericVal));
    }
  };

  return (
    <>
      <DistributionGraphContainerTop>
        <RadioSelectionTitle>{RANGE_SLIDER_CONTACT_SELECTION}</RadioSelectionTitle>
        <RadioGroupWrapper>
          <RadioLabelWrapper onClick={onMostLikelyCallback}>
            <Radio
              id={TEST_IDS.GraphMostLikelyRadioSelection}
              data-test={TEST_IDS.GraphMostLikelyRadioSelection}
              name="most-likely"
              size={20}
              isChecked={selectionType === DIST_GRAPH_RADIO_OPTIONS.MOST_LIKELY}
              value={selectionType}
            />
            <RadioSelectionText>{MOST_LIKELY_TO_CONVERT}</RadioSelectionText>
            <RadioSelectionTextSecondary>{`(${TOP_10_PERCENT})`}</RadioSelectionTextSecondary>
          </RadioLabelWrapper>
          <RadioLabelWrapper onClick={onLeastLikelyCallback}>
            <Radio
              id={TEST_IDS.GraphLeastLikelyRadioSelection}
              data-test={TEST_IDS.GraphLeastLikelyRadioSelection}
              name="least-likely"
              size={20}
              isChecked={selectionType === DIST_GRAPH_RADIO_OPTIONS.LEAST_LIKELY}
              value={selectionType}
            />
            <RadioSelectionText>{LEAST_LIKELY_TO_CONVERT}</RadioSelectionText>
            <RadioSelectionTextSecondary>{`(${BOTTOM_10_PERCENT})`}</RadioSelectionTextSecondary>
          </RadioLabelWrapper>
          <RadioLabelWrapper onClick={onCustomCallback}>
            <Radio
              id={TEST_IDS.GraphCustomRadioSelection}
              data-test={TEST_IDS.GraphCustomRadioSelection}
              name={DIST_GRAPH_RADIO_OPTIONS.CUSTOM}
              size={20}
              isChecked={selectionType === DIST_GRAPH_RADIO_OPTIONS.CUSTOM}
              value={selectionType}
            />
            <RadioSelectionText>{CUSTOM_RANGE}</RadioSelectionText>
            {selectionType === DIST_GRAPH_RADIO_OPTIONS.CUSTOM && (
              <>
                <RadioCustomRangeText>
                  <InputText
                    data-test={TEST_IDS.GraphMinRangeInput}
                    type={'number'}
                    minValue={0}
                    maxValue={100}
                    id="pg-custom-min-range"
                    hasError={isRangeMinInputValInvalid}
                    value={rangeMinInputVal.toString()}
                    onChange={onMinRangeInputChange}
                    Suffix={<StyledRadioPercentSign>%</StyledRadioPercentSign>}
                    width={'70'}
                  />
                </RadioCustomRangeText>
                <CustomDash>-</CustomDash>
                <InputText
                  data-test={TEST_IDS.GraphMaxRangeInput}
                  type={'number'}
                  minValue={0}
                  maxValue={100}
                  hasError={isRangeMaxInputValInvalid}
                  id="pg-custom-max-range"
                  value={rangeMaxInputVal.toString()}
                  onChange={onMaxRangeInputChange}
                  Suffix={<StyledRadioPercentSign>%</StyledRadioPercentSign>}
                  width={'70'}
                />
              </>
            )}
          </RadioLabelWrapper>
        </RadioGroupWrapper>
      </DistributionGraphContainerTop>
      <DistributionGraphContainerBottom>
        <HistogramContainer>
          <div>
            <DistributionGraph
              xMin={xMin}
              xMax={xMax}
              xData={xPoints}
              yMin={yMin}
              yMax={yMax}
              yData={yPoints}
              min={range.minPoint}
              max={range.maxPoint}
            />
            <SliderContainer key={selectionType}>
              <StyledSlider
                min={sliderMin}
                setMin={updateMin}
                max={sliderMax}
                setMax={updateMax}
                onAfterChange={() => {
                  setSelectionType(DIST_GRAPH_RADIO_OPTIONS.CUSTOM);
                  setRangeMinInputVal(range.minPoint);
                  setRangeMaxInputVal(range.maxPoint);
                }}
              />
            </SliderContainer>
          </div>
          <DistributionSelectionData
            isSegmentationButtonDisabled={isRangeMinInputValInvalid || isRangeMaxInputValInvalid}
            model={modelDetails as Required<PredictiveGoalModelDetails>}
            onSegmentationClick={handleNavigateToSegmentation}
            searchQuery={searchQuery}
          />
        </HistogramContainer>
      </DistributionGraphContainerBottom>
    </>
  );
};

export default DistributionChartWithRadioSelectors;
